<template>
  <div id="app">
    <loading :active="loading" class="loader" color="#131D65"></loading>
    <v-app>
      <survey-header v-if="!loading && isSurveyRoute" />
      <v-main v-if="!loading" :style="isHomeOrFinishPage ? { 'background-color': getAssets.primaryButtonBackground } : ''">
        <router-view />
        <cookies-banner v-if="showCookiesBanner" @close="closeCookies" />
        <google-analytics @init-google-analytics="initGoogleAnalytics()" />
      </v-main>
      <social-networks-footer v-if="!loading && !isApp && !isSurveyRoute" @open-cookies-banner="openCookies" @close="closeCookies" />
    </v-app>
  </div>
</template>

<script>
import { validCpf, validCnpj, telefoneValido, idadeMinima, idadeMaxima, dataValida, cidadeValida } from '@/config/custom-validators.js'
import Loading from 'vue-loading-overlay'
import { mapActions } from 'vuex'

const CONFIG_REFRESH_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

// Rotas onde trocar a config é seguro: não há formulário em andamento para re-renderizar embaixo
// do usuário. O /cadastro fica de fora de propósito, então uma mudança de config passa a valer
// antes de um cadastro começar, nunca no meio de um.
const CONFIG_REFRESH_ROUTES = ['home']

export default {
  components: {
    Loading,
    CookiesBanner: () => import('@/components/layout/CookiesBanner.vue'),
    SocialNetworksFooter: () => import('@/components/layout/SocialNetworksFooter.vue'),
    GoogleAnalytics: () => import('@/plugins/google-analytics/GoggleAnalytics.vue'),
    SurveyHeader: () => import('@/modules/survey/views/components/SurveyHeader.vue')
  },
  data: () => ({
    cookieBanner: false,
    pixelInit: false,
    googleAnalyticsInit: false,
    localLoading: false,
    configRefreshTimer: null
  }),
  computed: {
    isApp() {
      return this.$store.getters.IS_APP
    },
    hostname() {
      return this.$store.getters.GET_DOMAIN
    },
    isHomeOrFinishPage({ commmit, state }) {
      return ['home', 'finalizar', '404'].includes(this.$route.name)
    },
    getAssets() {
      return this.$store.getters.GET_ASSETS
    },
    loading() {
      return this.$store.getters.LOADING || this.localLoading
    },
    showCookiesBanner() {
      return (!!this.pixelCode || !!this.googleAnalyticsCode) && this.cookieBanner
    },
    pixelCode() {
      return this.$store.getters.PIXEL
    },
    googleAnalyticsCode() {
      return this.$store.getters.GOOGLE_ANALYTICS
    },
    isSmsRecover() {
      return this.$store.getters.IS_RECOVER_SMS
    },
    localStorageAccepted() {
      return window.localStorage.getItem('pixelCookiePermission') === 'true'
    },
    isSurveyRoute() {
      return ['survey-shortcode-entry', 'survey-id-entry', 'survey-form', 'survey-information'].includes(this.$route.name)
    },
    canApplyConfigRefresh() {
      return CONFIG_REFRESH_ROUTES.includes(this.$route.name)
    }
  },
  watch: {
    $route(to, from) {
      window.scrollTo(0, 0)
      if (!from.name || to.name !== 'nova-senha' || this.isSmsRecover) return
      this.$router.push({ path: '/home' })
    },
    // Ao voltar para uma rota segura (tipicamente o /home, entre um cadastro e o próximo), relê
    // na hora em vez de esperar o próximo tick do intervalo. Passa pelo mesmo gate do intervalo:
    // navegação programática pode acontecer com a aba oculta, e nesse caso o refresh fica para o
    // visibilitychange.
    '$route.name'() {
      this.refreshConfigWhenSafe()
    }
  },
  async created() {
    await this.loadHotsiteConfig()
    await this.handleRoutes()
    this.localLoading = false
  },
  async mounted() {
    // Setup validators
    this.$validator.extend('validCpf', validCpf)
    this.$validator.extend('validCnpj', validCnpj)
    this.$validator.extend('idadeMinima', idadeMinima)
    this.$validator.extend('cidadeValida', cidadeValida)
    this.$validator.extend('idadeMaxima', idadeMaxima)
    this.$validator.extend('dataValida', dataValida)
    this.$validator.extend('telefoneValido', telefoneValido)

    this.startConfigRefresh()
  },
  beforeDestroy() {
    this.stopConfigRefresh()
  },
  methods: {
    ...mapActions(['setHotsiteConfig', 'refreshHotsiteConfig']),
    // A tab can stay open far longer than a configuration change takes to publish, and the config
    // is otherwise only read once on created(). Poll while visible, and refresh as soon as a
    // hidden tab comes back, which is when a stale tab is most likely about to be used again.
    startConfigRefresh() {
      document.addEventListener('visibilitychange', this.handleConfigRefreshVisibility)
      this.configRefreshTimer = setInterval(this.refreshConfigWhenSafe, CONFIG_REFRESH_INTERVAL_MS)
    },
    handleConfigRefreshVisibility() {
      this.refreshConfigWhenSafe()
    },
    refreshConfigWhenSafe() {
      if (document.visibilityState !== 'visible') return
      if (!this.canApplyConfigRefresh) return
      this.refreshHotsiteConfig()
    },
    stopConfigRefresh() {
      document.removeEventListener('visibilitychange', this.handleConfigRefreshVisibility)
      if (this.configRefreshTimer) clearInterval(this.configRefreshTimer)
      this.configRefreshTimer = null
    },
    async loadHotsiteConfig() {
      const configData = document.documentElement.getAttribute('data-hotsite-config')
      const config = JSON.parse(configData)
      if (config?.data?.hotsite?.accountId) {
        this.setHotsiteConfig(config.data)
        this.$store.commit('LOADING', false)
        return
      }
      try {
        await this.$store.dispatch('getClient', this.hostname)
      } catch (error) {
        console.error(error)
        if (this.$route.path !== '/404') this.$router.push('404')
      }
    },
    tokenLogin(token) {
      this.$store.commit('LOADING', true)
      this.$store
        .dispatch('tokenLogin', { token })
        .then()
        .catch(err => {
          console.error(err)
          this.$snackbar({
            message: 'Token inválido.',
            snackbarColor: 'error'
          })
        })
        .finally(() => {
          this.$store.commit('LOADING', false)
        })
    },
    initFacebookPixel() {
      const permission = window.localStorage.getItem('pixelCookiePermission') === 'true'
      if (!this.showCookiesBanner && permission && !this.pixelInit) {
        window.fbq('init', this.pixelCode)
        window.fbq('track', 'PageView')
        this.pixelInit = true
      }
    },
    gtag() {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(arguments)
    },
    initGoogleAnalytics() {
      const permission = window.localStorage.getItem('googleAnalyticsCookiePermission') === 'true'
      if (!this.showCookiesBanner && permission && !this.googleAnalyticsInit) {
        window.gtag('js', new Date())

        window.gtag('config', this.googleAnalyticsCode)
        this.googleAnalyticsInit = true
      }
    },
    closeCookies(accept) {
      this.cookieBanner = false
      if (accept) {
        this.initFacebookPixel()
        this.initGoogleAnalytics()
      }
    },
    openCookies() {
      this.cookieBanner = true
    },
    async handleRoutes() {
      const unforcedHomeRoutes = ['/contato', '/confirm', '/finalizar', '/whatsapp', '/.well-known']
      const unforcedHomeBasePathsWithCookies = ['/e/', '/store-event-shortcode/', '/s', '/survey/', '/survey-form']
      const unforcedHomeBasePathsWithoutCookies = ['/w/']
      // Use reliable pathname throughout
      const pathname = window.location.pathname
      const urlParams = new URLSearchParams(window.location.search)

      if (this.$store.getters.ALLOWED_MODULES.captcha) {
        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${this.$store.getters.CAPTCHA_KEY}`
        script.async = true
        document.head.appendChild(script)
      }
      if (this.$store.getters.ROUTES.index && pathname === '/') {
        this.localLoading = true
        this.$router.replace({ path: this.$store.getters.ROUTES.index })
        // Sleep for 100 ms to ensure the first page is not shown
        await new Promise(resolve => setTimeout(resolve, 100))
        return
      }
      if (
        this.$store.getters.ROUTES.index &&
        this.$store.getters.ROUTES.allowed?.length &&
        !this.$store.getters.ROUTES.allowed.some(route => pathname.startsWith(route))
      ) {
        this.localLoading = true
        this.$router.replace({ path: this.$store.getters.ROUTES.index })
        // Sleep for 100 ms to ensure the first page is not shown
        await new Promise(resolve => setTimeout(resolve, 100))
        return
      }
      if (pathname === '/nova-senha') {
        if (!this.isSmsRecover) {
          this.localLoading = true
          await this.$store
            .dispatch('tokenValidation', { token: urlParams.get('token') })
            .then(() => {
              // keep user in this page
            })
            .catch(err => {
              console.error(err)
              this.$snackbar({
                message: 'Esse e-mail de alteração de senha já foi utilizado ou não é mais válido.',
                snackbarColor: 'error'
              })
              this.$router.push({ path: '/home' })
            })
          return
        }
      }
      if (pathname === '/recuperar-senha' && urlParams.get('external_auth') === 'true' && urlParams.get('document_no') !== null) {
        this.localLoading = true
        await this.$store
          .dispatch('checkUniqueUser', { username: urlParams.get('document_no') })
          .then(() => {
            // keep user in this page
            this.$store.commit('SET_IS_EXTERNAL_AUTH', true)
          })
          .catch(err => {
            console.error(err)
            this.$snackbar({
              message: 'Não foi possível iniciar a recuperação de senha.',
              snackbarColor: 'error'
            })
            this.$router.push({ path: '/home' })
          })
        return
      }
      // Pre cookie banner return
      if (unforcedHomeBasePathsWithoutCookies.some(route => pathname.startsWith(route))) {
        return
      }
      if (!this.localStorageAccepted) this.cookieBanner = true
      else {
        this.initFacebookPixel()
      }
      // Post cookie banner returns
      if (unforcedHomeRoutes.includes(pathname)) {
        return
      }
      if (unforcedHomeBasePathsWithCookies.some(route => pathname.startsWith(route))) {
        return
      }
      if (urlParams.get('is_app') !== 'true') {
        this.$router.push('home').catch(() => {})
      }
      if (urlParams.get('access_token')) {
        const token = urlParams.get('access_token')
        localStorage.setItem('token', token)
        this.$store.commit('SET_IS_APP', true)
        this.$store.commit('ACCESS_TOKEN', token)
        this.tokenLogin(token)
        this.$router.replace({
          ...this.$router.currentRoute,
          query: {
            token: undefined
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
.col-30 {
  width: 15%;
}
.col-50 {
  width: 50%;
}
.col-100 {
  width: 100%;
}
.button-height {
  height: 44px !important;
}
.flag {
  display: block;
  height: 30px;
}
.loader {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
.register-page {
  overflow-x: auto;
  background-color: white;
  height: calc(100% + 50px);
  .title-block {
    margin-top: 24px;
    margin-left: 40px;
    margin-bottom: 16px;
    .page-title {
      font-family: 'Roboto', medium;
      font-weight: 500;
      font-size: 36px;
      opacity: 0.86;
      margin: 0;
      color: rgba(0, 0, 0, 0.87);
    }
  }
  .form-box {
    font-family: 'Roboto', sans-serif;
    margin-left: 40px;
    margin-top: 24px;

    .required-fields {
      color: #6f6f6f;
      font-size: 16px;
      span {
        color: #1864ab;
      }
    }
    .form {
      .label {
        display: inline-block;
        margin-bottom: 4px;
        font-size: 18px;
        color: #6f6f6f;
        span {
          color: #1864ab;
        }
        &.disabled {
          color: rgba(0, 0, 0, 0.3);
          span {
            color: rgba(0, 0, 0, 0.3);
          }
        }
      }
      .input {
        &.v-input--is-disabled {
          .v-input__slot {
            border: 1px solid rgba(0, 0, 0, 0.3) !important;
          }
        }
        .v-input__slot {
          border: 1px solid #6f6f6f;
          &:hover {
            border: 1px solid #228be6 !important;
          }
        }
        &.v-input--is-focused {
          .v-input__slot {
            background-color: #fff9f2 !important;
            outline: none;
            border: 1px solid #228be6;
            input {
              color: #1864ab;
            }
          }
        }
        &.error--text {
          .v-input__slot {
            border: 1.6px solid #f03e3e;
          }
        }
        &.input-cep {
          .v-input--is-disabled:not(.v-input--is-readonly) {
            pointer-events: all !important;
          }
          .v-input__append-outer .v-icon,
          .v-input__prepend-outer .v-icon {
            pointer-events: all !important;
            color: rgba(0, 0, 0, 0.54) !important;
          }
        }
      }
      .register-button {
        font-family: 'Roboto', medium;
        font-weight: 500;
        font-size: 23px;
        color: #fff;
        width: 220px;
        height: 66px;
        border-radius: 5px;
        background-color: #1864ab;
        border-color: #707070;
        margin: 32px 0 16px;
      }
    }
  }
}
</style>
